import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  TrendingUp,
  Layers,
  Building2,
  FolderTree,
  Grid,
  Lightbulb,
  Zap,
  Flame,
  Percent,
  ArrowLeftRight,
  Sparkles,
  Clock,
  Bookmark,
  Radio,
  Handshake,
  LogOut,
  LogIn,
  ShieldCheck,
  Sliders,
  BookOpen,
  Mail,
  X,
  Sun,
  Moon,
  LucideIcon,
  Scale,
  CalendarDays,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useAuthPrompt } from '../../context/AuthPromptContext';
import { useTheme } from '../../context/ThemeContext';
import { useMarketStatus } from '../../utils/marketHours';

export interface NavItemConfig {
  path: string;
  name: string;
  shortName: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

export interface NavGroupConfig {
  title: string;
  items: NavItemConfig[];
}

export const NAV_GROUPS: NavGroupConfig[] = [
  {
    title: 'Market Overview',
    items: [
      { path: '/', name: 'Market Dashboard', shortName: 'Dashboard', icon: LayoutDashboard },
      { path: '/market-breadth', name: 'Market Breadth', shortName: 'Breadth', icon: TrendingUp },
      { path: '/index-heatmap', name: 'Index Heatmap', shortName: 'Heatmap', icon: Grid },
      { path: '/industry', name: 'Industry Analysis', shortName: 'Industry', icon: Building2 },
    ],
  },
  {
    title: 'Institutional Accumulation',
    items: [
      { path: '/overview', name: 'Accumulation Ladder', shortName: 'Ladder', icon: Layers },
      { path: '/accumulation', name: 'Accumulation List', shortName: 'Accumulation', icon: Sparkles },
    ],
  },
  {
    title: 'Scanners & Setups',
    items: [
      { path: '/opportunities', name: 'Breakout Scanner', shortName: 'Breakouts', icon: Lightbulb },
      { path: '/volumeshockers', name: 'Volume Shockers', shortName: 'V-Shockers', icon: Zap },
      { path: '/volume-streaks', name: 'Volume Streaks', shortName: 'V-Streaks', icon: Flame, adminOnly: true },
      { path: '/price-movers', name: '30D Price Movers', shortName: 'Price Movers', icon: Percent, adminOnly: true },
      { path: '/overboughtsold', name: 'Overbought / Oversold', shortName: 'OB / OS', icon: ArrowLeftRight },
    ],
  },
  {
    title: 'Research & Intelligence',
    items: [
      { path: '/history', name: 'Stock Explorer', shortName: 'Stocks', icon: Clock },
      { path: '/sector', name: 'Sector Explorer', shortName: 'Sectors', icon: FolderTree },
      { path: '/mtf-positioning', name: 'MTF Positioning', shortName: 'MTF', icon: Scale },
      { path: '/watchlist', name: 'Watchlist', shortName: 'Watchlist', icon: Bookmark },
      { path: '/bulk-block-deals', name: 'Bulk & Block Deals', shortName: 'Bulk Deals', icon: Handshake },
      { path: '/corporate-feeds', name: 'Corporate Feeds', shortName: 'Feeds', icon: Radio },
      {
        path: '/results-calendar',
        name: 'Results Calendar',
        shortName: 'Results',
        icon: CalendarDays,
        adminOnly: true,
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      { path: '/admin', name: 'Admin Control Panel', shortName: 'Control Panel', icon: Sliders, adminOnly: true },
    ],
  },
  {
    title: 'Support & Learning',
    items: [
      { path: '/guide', name: 'Trading Playbook', shortName: 'Playbook', icon: BookOpen },
      { path: '/contact-us', name: 'Contact Us', shortName: 'Contact', icon: Mail },
    ],
  },
];

const ROUTE_PRELOADERS: Record<string, () => Promise<unknown>> = {
  '/admin': () => import('../admin/AdminUsers'),
  '/admin/users': () => import('../admin/AdminUsers'),
  '/industry': () => import('../IndustryAnalysis'),
  '/index-heatmap': () => import('../IndexHeatmap'),
  '/overview': () => import('../Dashboard'),
  '/market-breadth': () => import('../MarketBreadth'),
  '/ema-chart': () => import('../EmaCountChart'),
  '/count': () => import('../PositiveNegativeCountChart'),
  '/accumulation-chart': () => import('../AccumulationCountChart'),
  '/opportunities': () => import('../Opportunities'),
  '/volumeshockers': () => import('../VolumeShockers'),
  '/accumulation': () => import('../Accumulation'),
  '/history': () => import('../Historical'),
  '/sector': () => import('../SectorExplorer'),
  '/watchlist': () => import('../Watchlist'),
  '/bulk-block-deals': () => import('../BulkBlockDeals'),
  '/mtf-positioning': () => import('../MtfPositioningPage'),
};

const handlePreload = (path: string) => {
  const preloader = ROUTE_PRELOADERS[path];
  if (preloader) {
    void preloader();
  }
};

interface SideNavProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

export const SideNav: React.FC<SideNavProps> = ({
  isCollapsed,
  isMobileOpen,
  onCloseMobile,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isAuthenticated, loading } = useAuth();
  const { openAuthPrompt } = useAuthPrompt();
  const { isDark, toggleTheme, isDiwali, toggleDiwali } = useTheme();

  const handleNavItemClick = (e: React.MouseEvent, item: NavItemConfig) => {
    onCloseMobile();
    const isPublic =
      item.path === '/' ||
      item.path === '/dashboard' ||
      item.path === '/summary' ||
      item.path === '/guide' ||
      item.path === '/playbook' ||
      item.path === '/contact-us';
    if (!isAuthenticated && !isPublic) {
      e.preventDefault();
      openAuthPrompt(item.path, item.name);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  const isRouteActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/summary';
    }
    if (path === '/market-breadth') {
      return (
        location.pathname === '/market-breadth' ||
        location.pathname === '/breadth' ||
        location.pathname === '/ema-chart' ||
        location.pathname === '/count' ||
        location.pathname === '/accumulation-chart'
      );
    }
    if (path === '/history') {
      return location.pathname.startsWith('/history');
    }
    if (path === '/sector') {
      return location.pathname.startsWith('/sector');
    }
    if (path === '/index-heatmap') {
      return location.pathname === '/index-heatmap' || location.pathname === '/indices-heatmap';
    }
    if (path === '/opportunities') {
      return location.pathname.startsWith('/opportunities');
    }
    if (path === '/guide') {
      return location.pathname === '/guide' || location.pathname === '/playbook';
    }
    if (path === '/results-calendar' || path === '/concall-calendar') {
      return (
        location.pathname === '/results-calendar' ||
        location.pathname === '/concall-calendar' ||
        location.pathname === '/earnings-calendar' ||
        location.pathname.startsWith('/concalls')
      );
    }
    if (path === '/admin' || path === '/admin/users') {
      return location.pathname.startsWith('/admin');
    }
    return location.pathname === path;
  };

  const marketStatus = useMarketStatus();
  const userName = user?.displayName || user?.username || user?.email?.split('@')[0] || 'Trader';
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <aside
      className={`ns-sidebar ${isCollapsed ? 'ns-sidebar--collapsed' : ''} ${isMobileOpen ? 'is-open' : ''}`}
      aria-label="Sidebar Navigation"
    >
      {/* Brand Header */}
      <div className="ns-sidebar__header">
        <Link to="/" className="ns-sidebar__brand" onClick={onCloseMobile} title="NiftySutra Home">
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
            alt="NiftySutra"
            className="ns-sidebar__brand-logo"
          />
          <div className="ns-sidebar__brand-copy">
            <span className="ns-sidebar__brand-title">
              NiftySutra
              {isDiwali && (
                <span className="ns-sidebar__brand-diya" title="Diwali Festive Season">
                  🪔
                </span>
              )}
            </span>
            <span className="ns-sidebar__brand-subtitle">Trading Intelligence</span>
          </div>
        </Link>
        <button
          type="button"
          onClick={onCloseMobile}
          className="ns-sidebar__close-mobile-btn"
          aria-label="Close navigation menu"
          title="Close menu"
        >
          <X size={18} />
        </button>
      </div>

      {/* Market Status Pill */}
      <div className="ns-sidebar__status-bar">
        <span
          className="ns-sidebar__status-badge"
          title={marketStatus.reason}
        >
          <span
            className={`ns-sidebar__status-dot ${
              marketStatus.isOpen
                ? ''
                : marketStatus.session === 'PreMarket'
                ? 'ns-sidebar__status-dot--premarket'
                : 'ns-sidebar__status-dot--closed'
            }`}
          />
          <span>{marketStatus.statusLabel}</span>
        </span>
        <span
          className="ns-sidebar__status-detail"
          title={marketStatus.reason}
          style={{
            maxWidth: '120px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: marketStatus.isHoliday ? 'var(--ns-warning, #f59e0b)' : 'inherit',
          }}
        >
          {marketStatus.subLabel}
        </span>
      </div>

      {/* Grouped Navigation Links */}
      <nav className="ns-sidebar__nav">
        {NAV_GROUPS.map((group) => {
          const visibleItems = group.items.filter((item) => !item.adminOnly || isAdmin);
          if (visibleItems.length === 0) return null;

          return (
            <div key={group.title} className="ns-sidebar__group">
              <span className="ns-sidebar__group-title">{group.title}</span>
              {visibleItems.map((item) => {
                const active = isRouteActive(item.path);
                const Icon = item.icon;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`ns-sidebar__item ${active ? 'is-active' : ''}`}
                    onClick={(e) => handleNavItemClick(e, item)}
                    onMouseEnter={() => handlePreload(item.path)}
                    onFocus={() => handlePreload(item.path)}
                    onTouchStart={() => handlePreload(item.path)}
                    title={isCollapsed ? item.name : undefined}
                    aria-current={active ? 'page' : undefined}
                  >
                    <span className="ns-sidebar__icon" aria-hidden="true">
                      <Icon size={18} />
                    </span>
                    <span className="ns-sidebar__label">{item.name}</span>
                    {item.adminOnly && (
                      <span className="ns-sidebar__admin-tag" title="Admin only">
                        Admin
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User Profile & Sign Out / In Footer */}
      <div className="ns-sidebar__footer">
        {loading ? (
          <div className="ns-sidebar__user" style={{ opacity: 0.5, cursor: 'default' }}>
            <div
              className="ns-sidebar__avatar"
              style={{ background: 'var(--ns-surface-2)', color: 'transparent' }}
              aria-hidden="true"
            >
              •
            </div>
            <div className="ns-sidebar__user-info">
              <span className="ns-sidebar__user-name" style={{ color: 'var(--ns-text-muted)', fontSize: '0.8rem' }}>
                Verifying session...
              </span>
            </div>
          </div>
        ) : isAuthenticated ? (
          <>
            <div className="ns-sidebar__user">
              <div className="ns-sidebar__avatar" aria-hidden="true">
                {userInitial}
              </div>
              <div className="ns-sidebar__user-info">
                <span className="ns-sidebar__user-name" title={userName}>
                  {userName}
                </span>
                <span className="ns-sidebar__user-role">
                  {isAdmin ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--ns-brand-text, #6366f1)', fontWeight: 600 }}>
                      <ShieldCheck size={11} /> Admin
                    </span>
                  ) : (
                    'Member'
                  )}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="ns-sidebar__logout-btn"
              title={isDark ? 'Switch to Light mode' : 'Switch to Dark mode'}
              aria-label={isDark ? 'Switch to Light mode' : 'Switch to Dark mode'}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {isAdmin && (
              <button
                type="button"
                onClick={() => toggleDiwali(user)}
                className={`ns-sidebar__logout-btn ns-sidebar__diwali-btn ${isDiwali ? 'is-active' : ''}`}
                title={isDiwali ? 'Admin: Disable Diwali Theme globally for all users' : 'Admin: Enable Diwali Theme globally for all users'}
                aria-label={isDiwali ? 'Admin: Disable Diwali Theme globally' : 'Admin: Enable Diwali Theme globally'}
              >
                <span style={{ fontSize: '0.85rem', lineHeight: 1 }} aria-hidden="true">
                  🪔
                </span>
              </button>
            )}

            <button
              type="button"
              onClick={handleLogout}
              className="ns-sidebar__logout-btn"
              title="Sign Out"
              aria-label="Sign Out"
            >
              <LogOut size={16} />
            </button>
          </>
        ) : (
          <>
            <div
              className="ns-sidebar__user"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                onCloseMobile();
                navigate('/login');
              }}
              title="Sign in to unlock full terminal"
            >
              <div
                className="ns-sidebar__avatar"
                style={{ background: 'var(--ns-brand)', color: '#ffffff', fontWeight: 700 }}
                aria-hidden="true"
              >
                G
              </div>
              <div className="ns-sidebar__user-info">
                <span className="ns-sidebar__user-name">Guest Trader</span>
                <span className="ns-sidebar__user-role" style={{ color: 'var(--ns-brand)' }}>
                  Free Dashboard
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="ns-sidebar__logout-btn"
              title={isDark ? 'Switch to Light mode' : 'Switch to Dark mode'}
              aria-label={isDark ? 'Switch to Light mode' : 'Switch to Dark mode'}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            <button
              type="button"
              onClick={() => {
                onCloseMobile();
                navigate('/login');
              }}
              className="ns-sidebar__logout-btn"
              style={{ color: 'var(--ns-brand)' }}
              title="Sign In"
              aria-label="Sign In"
            >
              <LogIn size={16} />
            </button>
          </>
        )}
      </div>
    </aside>
  );
};

export default SideNav;
